import get from "lodash/get";
import React from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import AnnouncementStatusForm from "~/components/Forms/AnnouncementStatusForm";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  getEndOfDayWithTimestamp,
  getFullTimeWithTimestamp,
} from "~/libs/getDateOptions";
import { getFormatDate } from "~/libs/getFormatDate";
import {
  AnnouncementStatus,
  useGetAnnouncementListQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";
import { DefaultRowControl } from "~/shared/ui/TableV2/ui/DefaultRowControl";

import AnnouncementFilter from "./Filter";
import {
  ANNOUNCEMENT_SORTABLE_COLUMNS,
  getAnnouncementColumns,
} from "./model/tableAdapter";
import { ANNOUNCEMENT_OPTIONS } from "./options";

import s from "./Announcement.less";

const statusFormId = "AnnouncementStatusForm";

const Announcement = () => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const history = useHistory();
  const { hasPermission } = usePermissions();
  const {
    count,
    page,
    openModal,
    currentId,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключи фильтров приходят из URL и не описаны в типе useGetLocationData
  const {
    title,
    text,
    singedBy,
    importance,
    status,
    createdAtFrom,
    createdAtTo,
  } = useGetLocationData() as Record<string, unknown>;

  // Бэкенд AnnouncementGetter.get не принимает order/orderBy,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: ANNOUNCEMENT_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && ANNOUNCEMENT_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const toDay = new Date();
  toDay.setHours(23, 59, 59, 0);

  const { data, loading, refetch } = useGetAnnouncementListQuery({
    variables: {
      count,
      page,
      createdAtFrom: createdAtFrom
        ? new Date(getFullTimeWithTimestamp(String(createdAtFrom)))
        : new Date("01.01.2000"),
      createdAtTo: createdAtTo
        ? new Date(getEndOfDayWithTimestamp(String(createdAtTo)))
        : createdAtFrom
          ? toDay
          : undefined,
      title: title ? String(title) : undefined,
      text: text ? String(text) : undefined,
      singedBy: singedBy ? String(singedBy) : undefined,
      importance: importance ? String(importance) : undefined,
      status: (status ? String(status) : undefined) as
        | AnnouncementStatus
        | undefined,
    },
    fetchPolicy: "no-cache",
  });

  const announcements = get(data, "AnnouncementGetter.get.items", []).map(
    ({
      id,
      importance: itemImportance,
      status: itemStatus,
      date,
      title: itemTitle,
      text: itemText,
      singedBy: itemSingedBy,
      canceledReason,
      Documents,
      createdAt,
      createdByUser,
    }) => ({
      id,
      importance: itemImportance,
      status: t(`announcement.${itemStatus}`),
      statusNotTable: itemStatus,
      date: getFormatDate(date, true),
      dateRaw: date ? new Date(date).getTime() : 0,
      title: itemTitle,
      text: itemText,
      singedBy: itemSingedBy,
      canceledReason,
      Documents,
      createdAt: getFormatDate(createdAt, true),
      createdAtRaw: createdAt ? new Date(createdAt).getTime() : 0,
      createdByUser: createdByUser?.fullname,
    }),
  );

  const totalAnnouncement = get(data, "AnnouncementGetter.get.total", 0);

  const oneAnnouncement =
    !!currentId && announcements.find((el) => el.id === currentId);

  const openAddModal = () => {
    history.push("add");
  };

  const handleCloseModal = () => {
    setDataReplace("openModal", "", "currentId", "");
  };

  const handleAnnouncementView = (record: { id: string | number }) => {
    history.push(`/announcement/viewing/${record.id}`);
  };

  const handleAnnouncementEditStatus = (record: { id: string | number }) => {
    setDataReplace("openModal", "Edit", "currentId", record.id);
  };

  const { handleCount, handlePage } = usePaginationParams();

  const handleAnnouncementDraftEdit = (record: { id: string | number }) => {
    history.push(`/announcement/${record.id}`);
  };

  const resolveAnnouncementRowEdit = (record: { statusNotTable?: string }) => {
    const statusToEdit = ["draft", "revoked"];
    if (!statusToEdit.includes(record.statusNotTable)) return undefined;
    return handleAnnouncementDraftEdit;
  };

  return (
    <Wrapper.Content
      title={t("announcement.announcement")}
      filterComponent={<AnnouncementFilter />}
      controlComponent={
        hasPermission("announcements.create") ? (
          <Button
            onClick={openAddModal}
            size={"medium"}
            icon={<PlusIcon size={16} />}
          >
            {t("ui.add")}
          </Button>
        ) : undefined
      }
    >
      <TableV2
        columns={getAnnouncementColumns(
          t,
          ANNOUNCEMENT_OPTIONS,
          tableSortField,
          sortType,
        )}
        dataSource={announcements}
        rowKey="id"
        loading={loading}
        rowActions={(record) => (
          <DefaultRowControl
            record={record}
            onViewDetails={
              hasPermission("announcements.get")
                ? handleAnnouncementView
                : undefined
            }
            onRowEdit={
              hasPermission("announcements.edit")
                ? resolveAnnouncementRowEdit(record)
                : undefined
            }
            onRowSettings={
              hasPermission("announcements.setStatus")
                ? handleAnnouncementEditStatus
                : undefined
            }
          />
        )}
        actionsFixed
        actionsPlacement="left"
        sortableColumns={ANNOUNCEMENT_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortField ? sortType : undefined}
        onSortChange={onSortChange}
        wrapperClassName={s.tableWrapper}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={totalAnnouncement}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={openModal === "Edit"}
        title={t("announcement.announcement_status")}
        onCancel={handleCloseModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: statusFormId,
          children: t("ui.save"),
          disabled:
            !!oneAnnouncement && oneAnnouncement.statusNotTable === "canceled",
        }}
      >
        <AnnouncementStatusForm
          setCloseModal={handleCloseModal}
          refetch={refetch}
          oneAnnouncement={oneAnnouncement}
          formId={statusFormId}
        />
      </Modal>
    </Wrapper.Content>
  );
};

export default Announcement;
